import { useDeveloperModeEnabled } from '@chipinside/devmode'
import { deburr } from 'lodash'
import { useEffect, useMemo, useState } from 'react'

import {
  clearRecentSearches,
  getRecentSearches,
  useRecentSearchScope,
} from '#/components/AdvancedSearch/Logic/recentSearches'
import {
  buildCategoryOptions,
  buildResultGroups,
  getResultItemLink,
} from '#/components/AdvancedSearch/Logic/searchResults'
import { useEntitySearch } from '#/components/AdvancedSearch/Logic/useEntity'
import { useKeysEvents } from '#/components/AdvancedSearch/Logic/useKeysEvents'
import { RecentSearchItem, SearchCategory } from '#/components/AdvancedSearch/types'
import { useFarmSlug } from '#/store/ducks/farm'
import useAppContext from '#/store/hooks/useAppContext'
import { ResolvedPage } from '#/store/services/Pages/PagesService'
import { usePages } from '#/store/services/Pages/usePages'

function normalizeString(string: string) {
  return deburr(string).toLowerCase().trim()
}

/** Coringa que lista todas as páginas em vez de filtrar por texto */
function isWildcardSearch(search: string): boolean {
  return /^%+$/.test(search)
}

function getSearchableText(page: ResolvedPage, devMode: boolean): string {
  const searchableFields = [page.title, page.subtitle, page.description, page.crumb]

  // Habilita busca por id no dev mode
  if (devMode) {
    searchableFields.push(page.id)
  }

  return searchableFields.filter(Boolean).join(' ')
}

export function useAdvancedSearch() {
  const pages = usePages()
  const context = useAppContext()
  const farmSlug = useFarmSlug()
  const isFarmContext = context === 'farm'
  const devMode = useDeveloperModeEnabled()

  const availablePages = useMemo(() => {
    // Habilita busca de todas as páginas em qualquer contexto no modo dev
    const devParams = {
      bypassPermissions: true,
      params: { farm: farmSlug ?? 'total' },
    }

    const defaultParams = { context } as const

    return pages.getAvailable(devMode ? devParams : defaultParams)
  }, [pages, context, devMode])

  const recentSearchScope = useRecentSearchScope()

  const searchablePage = useMemo(
    () =>
      availablePages
        .filter(page => page.searchable)
        .sort((firstPage, secondPage) => firstPage.title.localeCompare(secondPage.title)),
    [availablePages],
  )

  const [open, setOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [activeCategory, setActiveCategory] = useState<SearchCategory | null>(null)
  const [recentItems, setRecentItems] = useState<RecentSearchItem[]>([])
  // Search só lê value na montagem; mudar a key remonta o input e esvazia o texto
  const [searchResetKey, setSearchResetKey] = useState(0)

  const handleClose = () => {
    setSearchInput('')
    setActiveCategory(null)
    setActiveIndex(null)
    setOpen(false)
  }

  useEffect(() => {
    if (!open) return
    // O modal sempre abre sem item selecionado
    setActiveIndex(null)
    setRecentItems(getRecentSearches(recentSearchScope))
  }, [open, recentSearchScope.context, recentSearchScope.tenantSlug])

  const handleClearRecent = () => {
    clearRecentSearches(recentSearchScope)
    setRecentItems([])
  }

  const filteredResults = useMemo(() => {
    if (!searchInput) return searchablePage
    const search = normalizeString(searchInput)
    if (isWildcardSearch(search)) return searchablePage

    return searchablePage.filter(page =>
      normalizeString(getSearchableText(page, devMode)).includes(search),
    )
  }, [searchablePage, searchInput])

  const handleInputChange = (value: any) => {
    setSearchInput(value)
    setActiveIndex(null)
    setActiveCategory(null)
  }

  const handleClearSearch = () => {
    handleInputChange('')
    setSearchResetKey(key => key + 1)
  }

  const { data: entityData, isValidating: isValidatingEntities } = useEntitySearch(
    searchInput,
    isFarmContext,
  )

  const categoryOptions = useMemo(
    () =>
      buildCategoryOptions({
        entityData,
        isFarmContext,
        pages: filteredResults,
      }),
    [entityData, filteredResults, isFarmContext],
  )

  useEffect(() => {
    if (activeCategory && !categoryOptions.some(option => option.key === activeCategory)) {
      setActiveCategory(null)
    }
  }, [activeCategory, categoryOptions])

  const groups = useMemo(
    () =>
      buildResultGroups({
        activeCategory,
        entityData,
        isFarmContext,
        pages: filteredResults,
        recentItems,
        searchInput,
      }),
    [activeCategory, entityData, filteredResults, isFarmContext, recentItems, searchInput],
  )
  const visibleItems = useMemo(() => groups.flatMap(group => group.items), [groups])
  const navigableItems = useMemo(
    () => visibleItems.map(result => ({ link: getResultItemLink(result) })),
    [visibleItems],
  )

  const activeOption = categoryOptions.find(option => option.key === activeCategory)
  const visibleCount = activeCategory
    ? (activeOption?.count ?? 0)
    : categoryOptions.reduce((sum, option) => sum + option.count, 0)

  useKeysEvents({
    activeIndex,
    filteredResults: navigableItems,
    open,
    setActiveIndex,
    setOpen,
  })

  return {
    activeCategory,
    activeIndex,
    categoryOptions,
    groups,
    handleClearRecent,
    handleClearSearch,
    handleClose,
    handleInputChange,
    isFarmContext,
    isValidatingEntities,
    open,
    recentSearchScope,
    searchInput,
    searchResetKey,
    setActiveCategory,
    setActiveIndex,
    setOpen,
    visibleCount,
  }
}
